/**
 * tCms — i18n key lookup with CMS override layer.
 *
 * Usage in a component:
 *   const locale = i18n.language as Locale
 *   <h2>{tCms('home.news.title', locale)}</h2>
 *
 * Lookup chain:
 *   1. cmsOverrides[locale][key]   ← CMS override (from generated TS file)
 *   2. i18next.t(key, { lng: locale })   ← existing JSON fallback
 *   3. '' (empty string)
 */

import i18n from '@/i18n'
import type { Locale } from '@/admin/api'
import { cmsOverrides } from '@/content/cms/overrides.generated'

/** Per-locale override map. Each key is `pageSlug::slotKey` per the build script. */
type OverrideMap = Partial<Record<Locale, string>>

const KEY_SEP = '::'

function makeKey(pageSlug: string, slotKey: string): string {
  return `${pageSlug}${KEY_SEP}${slotKey}`
}

/**
 * Resolve a CMS-overridable i18n key.
 * @param pageSlug e.g. 'home'
 * @param slotKey  e.g. 'home.news.title'
 * @param locale   e.g. 'zh-Hant'
 */
export function tCms(pageSlug: string, slotKey: string, locale: Locale): string {
  const k = makeKey(pageSlug, slotKey)
  const fromCms = (cmsOverrides[k] as OverrideMap | undefined)?.[locale]
  if (typeof fromCms === 'string' && fromCms.length > 0) return fromCms
  return i18n.t(slotKey, { lng: locale }) as string
}

/** Has a CMS override been set for this key + locale? */
export function hasOverride(pageSlug: string, slotKey: string, locale: Locale): boolean {
  const k = makeKey(pageSlug, slotKey)
  const v = (cmsOverrides[k] as OverrideMap | undefined)?.[locale]
  return typeof v === 'string' && v.length > 0
}
